/**
 * Timer and time-tracking API client.
 * Handles start/stop, time entries, and active timer queries.
 */

import { fetchApi, getApiBase, getAuthHeaders } from './fetch';

export interface TimerStartResult {
  entryId: string;
  taskId: string;
  startedAt: string;
}

export interface TimeEntry {
  entryId: string;
  taskId: string;
  startedAt: string;
  stoppedAt: string;
  durationSeconds: number;
}

export interface TimeEntriesResponse {
  entries: Array<{
    id: string;
    startedAt: string;
    stoppedAt: string | null;
    durationSeconds: number | null;
  }>;
  totalSeconds: number;
}

export interface ActiveTimer {
  entryId: string;
  taskId: string;
  taskTitle: string;
  startedAt: string;
}

export async function startTimer(taskId: string): Promise<TimerStartResult> {
  return fetchApi<TimerStartResult>(`/tasks/${taskId}/timer/start`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function stopTimer(taskId: string): Promise<TimeEntry> {
  return fetchApi<TimeEntry>(`/tasks/${taskId}/timer/stop`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function getTimeEntries(taskId: string): Promise<TimeEntriesResponse> {
  return fetchApi<TimeEntriesResponse>(`/tasks/${taskId}/time-entries`);
}

export async function addManualEntry(
  taskId: string,
  startedAt: string,
  stoppedAt: string,
): Promise<TimeEntry> {
  return fetchApi<TimeEntry>(`/tasks/${taskId}/time-entries`, {
    method: 'POST',
    body: JSON.stringify({ startedAt, stoppedAt }),
  });
}

/**
 * Returns the active timer, or null if no timer is running (204 No Content).
 *
 * fetchApi returns `{} as T` for 204. We detect this by checking whether
 * the returned object is missing the `entryId` field that every real ActiveTimer has.
 */
export async function getActiveTimer(): Promise<ActiveTimer | null> {
  // Use raw fetch so we can inspect the status code directly and avoid
  // the empty-object cast that fetchApi does for 204.
  const apiBase = getApiBase();
  const authHeaders = getAuthHeaders();

  const response = await fetch(`${apiBase}/timer/active`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
    },
  });

  if (response.status === 204) return null;

  if (!response.ok) {
    // Propagate auth events the same way fetchApi does
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent('danny:session-expired'));
    }
    const errorData = await response.json().catch(() => ({}));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- errorData is unknown JSON from server
    const message = (errorData as any).message || `HTTP ${response.status}`;
    throw new Error(message);
  }

  return response.json() as Promise<ActiveTimer>;
}
